﻿import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import dayjs from 'dayjs';
import Chart from 'react-apexcharts';

import Content_Container from '@Elements/Content_Container';

import {
    Exception_Count_Chart_Props,
    Client_Website_History_Type
} from '@Util/Types';

export default function Exception_Count_Chart(props: Exception_Count_Chart_Props): JSX.Element {
    const [t] = useTranslation();

    const chart_data = useMemo(() => {
        if (!props?.website_data || props?.website_data?.length < 1) {
            return null;
        }

        const exceptionCountSeries = {
            name: 'Exception Count',
            data: props.website_data.map((website: Client_Website_History_Type) => website.exceptionCount),
            color: '#C35050'
        };

        const responseTimeSeries = {
            name: 'Response Time',
            data: props.website_data.map((website: Client_Website_History_Type) => website.responseTime),
            color: '#44abe1'
        };

        return [exceptionCountSeries, responseTimeSeries];
    }, [props.website_data]);

    if (!chart_data) {
        return null;
    }

    return (
        <section className="Module">
            <header className="Module_Header">
                <h2 className="Headline2">{t('Text.ExceptionsAndResponseTime')}</h2>
            </header>
            <Content_Container>
                {
                    chart_data?.length ? (
                        <Chart
                            options={{
                                chart: {
                                    type: 'area',
                                    stacked: false,
                                    toolbar: {
                                        show: false
                                    }
                                },
                                dataLabels: {
                                    enabled: false
                                },
                                plotOptions: {
                                    bar: {
                                        columnWidth: "20%"
                                    }
                                },
                                xaxis: {
                                    categories: props.website_data.map((website: Client_Website_History_Type) => dayjs(website.dateTime).format('YYYY-MM-DD HH:mm')),
                                    labels: {
                                        style: {
                                            colors: '#929292'
                                        }
                                    }
                                },
                                yaxis: [
                                    {
                                        min: 0,
                                    },
                                    {
                                        opposite: true,
                                    }
                                ],
                                stroke: {
                                    width: [4, 4],
                                    curve: 'smooth'
                                },
                                legend: {
                                    position: 'bottom',
                                    labels: {
                                        colors: '#929292'
                                    }
                                },
                                fill: {
                                    type: "gradient",
                                    gradient: {
                                        shadeIntensity: .15,
                                        opacityFrom: 0.5,
                                        opacityTo: 0,
                                        stops: [0, 80, 100]
                                    },
                                },
                                grid: {
                                    show: false,
                                },
                                tooltip: {
                                    theme: 'dark'
                                },
                            }}
                            series={chart_data}
                            height="550"
                            type="area"
                        />
                    ) : (
                        <Skeleton
                            width={'100%'}
                            height={550}
                            baseColor="#1c1e21"
                            highlightColor="#282c32"
                        />
                    )}
            </Content_Container>
        </section>
    )
}